import { jsonrepair } from 'jsonrepair';
import jsPDF from 'jspdf';

import { addSvgElement } from './addSvg';
import { LOGO_HEIGHT, LOGO_MARGIN, LOGO_WIDTH } from './constants';
import { logoBase64 } from './logoBase64';

export const getProp = (obj: unknown, propPath: string | string[]): unknown => {
  if (!obj || typeof obj !== 'object') return;

  const props = Array.isArray(propPath) ? propPath : propPath.split('.');
  const currentProp = props.shift();
  if (!currentProp || !(currentProp in obj)) return;

  const value = (obj as Record<typeof currentProp, unknown>)[currentProp];
  return props.length > 0 ? getProp(value, props) : value;
};

export const getRgbFromString = (rgbString: string): [number, number, number] | null => {
  const results = /rgba?\((\d+),\s*(\d+),\s*(\d+)[^\)]*\)/.exec(rgbString);
  if (!results) return null;
  return [
    Number.parseInt(results[1]),
    Number.parseInt(results[2]),
    Number.parseInt(results[3]),
  ];
};

export const parseRGB = (hexString: string): [number, number, number] | null => {
  //this is a hack fix for the white color issue being downloaded as black
  const hex =
    hexString === '#white' ? '#000000' : hexString === 'white' ? '#ffffff' : hexString;
  if (hex.startsWith('rgb')) return getRgbFromString(hex);

  let hexColor = hex.startsWith('#') ? hex.slice(1) : '000';
  if (hexColor.length === 3) {
    hexColor = `${hexColor[0]}${hexColor[0]}${hexColor[1]}${hexColor[1]}${hexColor[2]}${hexColor[2]}`;
  }
  const hexColors = /([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hexColor);
  if (hexColors) {
    return [
      Number.parseInt(hexColors[1], 16),
      Number.parseInt(hexColors[2], 16),
      Number.parseInt(hexColors[3], 16),
    ];
  }

  return null;
};

export const setRotation = (
  pdf: jsPDF,
  angleDeg: number,
  pivotX: number,
  pivotY: number,
) => {
  const angleRad = ((2 * Math.PI) / 360) * angleDeg;
  const a = Math.cos(angleRad);
  const b = -Math.sin(angleRad);
  const c = Math.sin(angleRad);
  const d = Math.cos(angleRad);
  const pageHeight = (getProp(
    pdf.getCurrentPageInfo().pageContext.mediaBox,
    'topRightY',
  ) ?? 0) as number;
  const pivotYFromBottom = pageHeight - pivotY;
  const rotationMatrix = pdf.unitMatrix
    .multiply(pdf.Matrix(1, 0, 0, 1, pivotX, pivotYFromBottom))
    .multiply(pdf.Matrix(a, b, c, d, 0, 0))
    .multiply(pdf.Matrix(1, 0, 0, 1, -pivotX, -pivotYFromBottom));
  pdf.setCurrentTransformationMatrix(rotationMatrix);
};

export const addLogo = async (pdf: jsPDF, slideWidth: number) => {
  pdf.saveGraphicsState();
  await addSvgElement(
    pdf,
    Buffer.from(logoBase64, 'base64').toString('utf-8'),
    slideWidth - LOGO_WIDTH - LOGO_MARGIN,
    LOGO_MARGIN,
    LOGO_WIDTH,
    LOGO_HEIGHT,
  );
  pdf.restoreGraphicsState();
};

export const parseJSON = (json: unknown) => {
  try {
    return typeof json === 'string' ? JSON.parse(jsonrepair(json)) : null;
  } catch (err) {
    return null;
  }
};

export const addPollLine = async (
  pdf: jsPDF,
  x: number,
  y: number,
  width: number,
  color: number[],
) => {
  const addShadowLine = () => {
    pdf.saveGraphicsState();
    pdf.setLineWidth(5);
    pdf.setLineCap('round');
    pdf.setDrawColor('#333');
    const newGState = pdf.GState({
      'stroke-opacity': 0.1,
    });
    pdf.setGState(newGState);
    pdf.line(x - 2, y - 2, x + width + 3, y - 2);
    pdf.restoreGraphicsState();
  };

  const addLine = () => {
    pdf.saveGraphicsState();
    pdf.setLineWidth(5);
    pdf.setLineCap('round');
    pdf.setDrawColor(color[0], color[1], color[2]);
    pdf.line(x, y, x + width, y);
    pdf.restoreGraphicsState();
  };

  addShadowLine();
  addLine();
};
